import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { money } from "@/components/opportunities/shared";
import { formatDate, formatDateTime } from "@/lib/dates";
import { CircleNotch, FileXls, FilePdf, FloppyDisk, DownloadSimple, CaretDown, CaretRight } from "@phosphor-icons/react";
import ProspectReportBuilder from "@/components/reports/ProspectReportBuilder";

const ANY = "__any__";
// Only the filters relevant to each report type
const REPORT_FILTERS = {
  pipeline_summary: ["date", "owner", "stage", "division", "lead_origin", "archived", "q"],
  opportunity_detail: ["date", "owner", "stage", "division", "lead_origin", "archived", "q"],
  salesperson_performance: ["owner"],
  activity_follow_up: ["date", "owner", "activity_status"],
  prospect_portfolio: ["owner", "archived", "q"],
  client_won_business: ["date", "owner", "division", "q"],
  commission_ledger: ["owner", "commission_status"],
  management_snapshot: [],
};
const EXTERNAL_OK = ["pipeline_summary", "opportunity_detail", "prospect_portfolio", "client_won_business", "activity_follow_up"];
// Drill-down targets per report type. Detail routes are permission-scoped on the
// backend (get_*_or_404), so an unowned id can never be opened through a link.
const DRILL = {
  opportunity_detail: (r) => `/opportunities/${r.id}`,
  client_won_business: (r) => `/opportunities/${r.id}`,
  prospect_portfolio: (r) => `/prospects/${r.id}`,
  activity_follow_up: (r) => (r.prospect_id ? `/prospects/${r.prospect_id}` : null),
  commission_ledger: (r) => (r.opportunity_id ? `/opportunities/${r.opportunity_id}` : null),
};

function fmt(v, type) {
  if (v === null || v === undefined || v === "") return "—";
  if (type === "currency" && typeof v === "number") return money(v);
  if (type === "percent" && typeof v === "number") return `${v.toFixed(1)}%`;
  if (type === "date") return shortDate(v);
  return String(v);
}

// Human-readable filter labels for report headers (never internal field names).
const BASIS_LABELS = { created_at: "Date created", won_date: "Date won", target_close_date: "Target close date" };
const periodText = (p) => (p.from || p.to
  ? `${p.from ? shortDate(p.from) : "…"} → ${p.to ? shortDate(p.to) : "…"}`
  : "All dates");

// Reports use explicit MM/DD/YY dates per business requirement.
function shortDate(v) {
  const d = formatDate(v);
  return d.replace(/\/(\d{2})(\d{2})$/, "/$2");
}

async function fetchRefsInto(setRefs, isActive = () => true) {
  try {
    const [stages, divisions, users, defs, exports] = await Promise.all([
      api.get("/reference/pipeline-stages"), api.get("/reference/service-divisions"),
      api.get("/users/options"), api.get("/report-definitions"), api.get("/reports/exports"),
    ]);
    if (!isActive()) return;
    setRefs({ stages: stages.data, divisions: divisions.data, users: users.data, defs: defs.data, exports: exports.data });
  } catch (e) {
    if (isActive()) toast.error("Report options could not be loaded.");
  }
}

export default function Reports() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [active, setActive] = useState(null);
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [refs, setRefs] = useState({ stages: [], divisions: [], users: [], defs: [], exports: [] });
  const [profile, setProfile] = useState("internal");
  // loadRefs is reused by save/delete/export handlers to refresh reference
  // data. fetchRefsInto (module scope) and setRefs (state setter) are stable,
  // so loadRefs is created once ([]) and never changes identity.
  const loadRefs = useCallback((isActive) => fetchRefsInto(setRefs, isActive), []);
  // Single initialization effect: no reactive inputs beyond the stable
  // loadRefs, one request per endpoint on mount, and a cancellation flag so
  // an unmounted component is never updated. No lint suppression needed.
  useEffect(() => {
    let active = true;
    loadRefs(() => active);
    api.get("/reports/types")
      .then((r) => { if (active) setTypes(r.data); })
      .catch(() => { if (active) toast.error("Reports unavailable"); });
    return () => { active = false; };
  }, [loadRefs]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const cleanFilters = () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== ANY));

  const run = async (type = active, f = cleanFilters()) => {
    setRunning(true);
    setData(null);
    try {
      const { data: d } = await api.post("/reports/run", { report_type: type, filters: f });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const doExport = async (format) => {
    try {
      const { data: exp } = await api.post("/reports/export", { report_type: active, filters: cleanFilters(), format, profile });
      toast.success(`${format.toUpperCase()} export ready`);
      loadRefs();
      // Anchor click (not window.open) — survives popup blockers; cookie auth + Content-Disposition
      const a = document.createElement("a");
      a.href = `${api.defaults.baseURL}/reports/exports/${exp.id}/download`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveDef = async () => {
    const name = window.prompt("Saved report name:");
    if (!name) return;
    try {
      await api.post("/report-definitions", { report_type: active, name, filters: cleanFilters(), org_wide: false });
      toast.success("Report definition saved");
      loadRefs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteDef = async (id) => {
    try {
      await api.delete(`/report-definitions/${id}`);
      toast.success("Saved report removed");
      loadRefs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const F = REPORT_FILTERS[active] || [];
  const [showStandard, setShowStandard] = useState(false);
  const [showExports, setShowExports] = useState(false);
  return (
    <div className="p-6 sm:p-8" data-testid="reports-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Insight</p>
      <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">Reports</h1>

      {/* Batch H: the Prospect Report Builder + Preview are the principal working areas. */}
      <ProspectReportBuilder />

      <div className="mt-6">
        <button onClick={() => setShowStandard(!showStandard)}
          className="flex items-center gap-1.5 text-base font-bold text-[#032852] hover:text-[#569A1F] md:text-lg"
          data-testid="standard-reports-toggle">
          {showStandard ? <CaretDown size={16} /> : <CaretRight size={16} />} Standard Reports
        </button>
      </div>
      {showStandard && (<>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="report-catalog">
        {types.map((t) => (
          <button key={t.key} onClick={() => { setActive(t.key); setFilters({}); setData(null); }}
            className={`rounded-sm border p-3 text-left text-sm font-semibold ${active === t.key ? "border-[#569A1F] bg-[#569A1F]/5 text-[#032852]" : "border-[#E4E4E7] bg-white text-[#52525B] hover:border-[#032852]/40"}`}
            data-testid={`report-card-${t.key}`}>{t.name}</button>
        ))}
      </div>

      {refs.defs.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="saved-definitions">
          {refs.defs.map((d) => (
            <span key={d.id} className="inline-flex items-center rounded-sm bg-[#032852]/5 text-xs font-semibold text-[#032852]">
              <button onClick={() => { setActive(d.report_type); setFilters(d.filters || {}); run(d.report_type, d.filters || {}); }}
                className="px-3 py-1.5 hover:bg-[#032852]/10"
                data-testid={`saved-def-${d.id}`}>★ {d.name}{d.org_wide ? " (org)" : ""}</button>
              <button onClick={() => deleteDef(d.id)} title="Delete saved report"
                className="px-2 py-1.5 text-[#6D6D70] hover:text-red-600"
                data-testid={`delete-def-${d.id}`}>×</button>
            </span>
          ))}
        </div>
      )}

      {active && (
        <div className="mt-4 rounded-sm border border-[#E4E4E7] bg-white p-4">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
            {F.includes("date") && (<>
              <DatePicker value={filters.date_from || ""} onChange={set("date_from")} placeholder="From" className="sm:w-36" testId="rf-date-from" />
              <DatePicker value={filters.date_to || ""} onChange={set("date_to")} placeholder="To" className="sm:w-36" testId="rf-date-to" />
              <Select value={filters.date_basis || "created_at"} onValueChange={set("date_basis")}>
                <SelectTrigger className="rounded-sm sm:w-40" data-testid="rf-date-basis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Created date</SelectItem>
                  <SelectItem value="won_date">Won date</SelectItem>
                  <SelectItem value="target_close_date">Target close</SelectItem>
                </SelectContent>
              </Select>
            </>)}
            {F.includes("owner") && isAdmin && (
              <Select value={filters.owner_id || ANY} onValueChange={set("owner_id")}>
                <SelectTrigger className="rounded-sm sm:w-40" data-testid="rf-owner"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>All owners</SelectItem>
                  {refs.users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>)}
            {F.includes("stage") && (
              <Select value={filters.stage_id || ANY} onValueChange={set("stage_id")}>
                <SelectTrigger className="rounded-sm sm:w-44" data-testid="rf-stage"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>All stages</SelectItem>
                  {refs.stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>)}
            {F.includes("division") && (
              <Select value={filters.service_division_id || ANY} onValueChange={set("service_division_id")}>
                <SelectTrigger className="rounded-sm sm:w-44" data-testid="rf-division"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>All divisions</SelectItem>
                  {refs.divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>)}
            {F.includes("lead_origin") && (
              <Select value={filters.lead_origin || ANY} onValueChange={set("lead_origin")}>
                <SelectTrigger className="rounded-sm sm:w-44" data-testid="rf-origin"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>All origins</SelectItem>
                  <SelectItem value="shirley_originated">Self-Originated</SelectItem>
                  <SelectItem value="company_assigned">Company-Assigned</SelectItem>
                  <SelectItem value="tag_work">Tag Work</SelectItem></SelectContent>
              </Select>)}
            {F.includes("activity_status") && (
              <Select value={filters.activity_status || ANY} onValueChange={set("activity_status")}>
                <SelectTrigger className="rounded-sm sm:w-40" data-testid="rf-activity-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>All statuses</SelectItem>
                  {["due today", "upcoming", "completed", "overdue"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>)}
            {F.includes("commission_status") && (
              <Select value={filters.commission_status || ANY} onValueChange={set("commission_status")}>
                <SelectTrigger className="rounded-sm sm:w-40" data-testid="rf-commission-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>All statuses</SelectItem>
                  {["projected", "approved", "earned", "paid", "voided"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>)}
            {F.includes("archived") && (
              <Select value={filters.archived || "active"} onValueChange={set("archived")}>
                <SelectTrigger className="rounded-sm sm:w-36" data-testid="rf-archived"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="all">Include archived</SelectItem><SelectItem value="true">Archived only</SelectItem></SelectContent>
              </Select>)}
            {F.includes("q") && <Input value={filters.q || ""} onChange={set("q")} placeholder="Search…" className="rounded-sm sm:w-44" data-testid="rf-search" />}
            <Button onClick={() => run()} disabled={running} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="run-report-btn">
              {running ? <CircleNotch size={16} className="animate-spin" /> : "Run report"}
            </Button>
          </div>
        </div>
      )}

      {data && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-[#E4E4E7] bg-white p-4" data-testid="report-meta">
            <div className="text-xs text-[#52525B]">
              <p className="font-heading text-lg font-black text-[#032852]">{data.meta.report_name}</p>
              <p>Reporting period: {periodText(data.meta.period)} · Date field: {BASIS_LABELS[data.meta.date_basis] || data.meta.date_basis} · Pulled {formatDateTime(data.meta.generated_at)} by {data.meta.generated_by} · <b data-testid="report-record-count">{data.scope_count_label || `${data.meta.record_count} records`}</b></p>
              {data.note && (
                <p className="mt-1.5 max-w-3xl rounded-sm bg-[#032852]/5 px-2.5 py-1.5 text-[11px] leading-snug text-[#032852]" data-testid="report-note">
                  {data.note}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={saveDef} data-testid="save-definition-btn"><FloppyDisk size={15} /> Save</Button>
              {isAdmin && EXTERNAL_OK.includes(active) && (
                <Select value={profile} onValueChange={setProfile}>
                  <SelectTrigger className="h-9 w-32 rounded-sm text-xs" data-testid="export-profile-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="internal">Internal</SelectItem><SelectItem value="external">External</SelectItem></SelectContent>
                </Select>)}
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => doExport("xlsx")} data-testid="export-xlsx-btn"><FileXls size={15} /> XLSX</Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => doExport("pdf")} data-testid="export-pdf-btn"><FilePdf size={15} /> PDF</Button>
            </div>
          </div>
          {data.rows.length === 0 ? (
            <p className="mt-3 rounded-sm border border-dashed border-[#E4E4E7] bg-white py-10 text-center text-sm text-[#6D6D70]" data-testid="report-empty">No records match these filters.</p>
          ) : (
            <div className="mt-3 max-h-[70vh] overflow-auto rounded-sm border border-[#E4E4E7] bg-white" data-testid="report-table">
              <table className="w-full text-left text-sm">
                {/* Sticky heading: column titles stay visible (repeat) while scrolling long result sets. */}
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_#E4E4E7]"><tr className="text-[10px] font-bold uppercase text-[#6D6D70]">
                  {data.columns.map((c) => <th key={c.key} className="px-3 py-2.5">{c.label}</th>)}</tr></thead>
                <tbody>
                  {data.rows.map((r) => {
                    const to = DRILL[data.meta.report_type]?.(r) || null;
                    return (
                      <tr key={r.id} onClick={to ? () => navigate(to) : undefined}
                        className={`border-b border-[#F4F4F5] last:border-0 ${to ? "cursor-pointer hover:bg-[#FAFAFA]" : ""}`}
                        data-testid={`report-row-${r.id}`}>
                        {data.columns.map((c) => <td key={c.key} className="px-3 py-2 text-[#52525B]">{fmt(r[c.key], c.type)}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="border-t border-[#E4E4E7] bg-[#032852]/5 px-3 py-2 text-xs font-bold text-[#032852]" data-testid="report-totals">
                {Object.entries(data.totals).map(([k, v]) => <span key={k} className="mr-4">{k.replace(/_/g, " ")}: {typeof v === "number" && !Number.isInteger(v) ? money(v) : String(v)}</span>)}
              </div>
            </div>
          )}
        </div>
      )}
      </>)}

      <div className="mt-6">
        <button onClick={() => setShowExports(!showExports)}
          className="flex items-center gap-1.5 text-base font-bold text-[#032852] hover:text-[#569A1F] md:text-lg"
          data-testid="recent-exports-toggle">
          {showExports ? <CaretDown size={16} /> : <CaretRight size={16} />} Recent Exports ({refs.exports.length})
        </button>
        {showExports && (
        <div className="mt-2 space-y-1" data-testid="exports-history">
          {refs.exports.slice(0, 10).map((x) => (
            <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-[#E4E4E7] bg-white px-3 py-2 text-xs" data-testid={`export-row-${x.id}`}>
              <span className="text-[#52525B]">{x.file_name || `${x.report_type}.${x.file_type}`} · {x.file_type.toUpperCase()} · {x.profile}{x.profile === "external" ? " ⚠" : ""} · {x.status} · {x.record_count} records · {x.requested_by_name}</span>
              {x.status === "completed" && x.expires_at > new Date().toISOString() && (
                <a href={`${api.defaults.baseURL}/reports/exports/${x.id}/download`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 font-bold text-[#569A1F]" data-testid={`download-export-${x.id}`}>
                  <DownloadSimple size={13} /> Download</a>)}
            </div>))}
          {refs.exports.length === 0 && <p className="text-xs text-[#6D6D70]">No exports yet.</p>}
        </div>
        )}
      </div>
    </div>
  );
}
